import { Reducer } from 'react';
import { ISnackbarInput } from '../NewUI/Components/CustomUIElements/StyledSnackbar';
import { searchTableColumns, actionDrawerModals } from './config';
import { TCandidate } from './types';
import { ISearchFilters, ITempFilters, initialSearchFilters } from './AISearch/SearchFilters';
import {
  TPaginationState,
  TTableState,
  TableActionDrawerAction,
  TableActionItemAction,
  TableColumnAction,
  TableFilter,
  TableFilterAction,
  TablePaginationAction,
  TableScrollAction,
  TableSelectionAction
} from '../NewUI/Components/GenericTable/types';
import {
  addSelected,
  removeSelected,
  addAllPageItems,
  removeAllPageItems
} from '../NewUI/Components/GenericTable/tableHelper';
import { ICountries } from '../NewUI/Components/sharedTypes';
import {
  DateRangeAction,
  TempDateRange
} from '../NewUI/Components/GenericTable/Filters/DateRangeFilter';
import moment from 'moment';

type TLPMatchModalStep = 'select' | 'loading' | 'edit' | 'error';

export interface EngageState {
  selectedTab: number;
  indicatorWidth: number;
  snackbarState: ISnackbarInput;
  searchQuery: string;
  booleanQuery: string | null;
  searchType: 'string_match' | 'tlp_match' | 'none';
  searchSettings: TPaginationState<(typeof searchTableColumns)[number]['id']>;
  searchFilters: TableFilter;
  tempSearchFilters: ITempFilters;
  searchTable: TTableState<TCandidate, typeof searchTableColumns>;
  dateFieldRange: TempDateRange;
  tlpMatchModalState: {
    open: boolean;
    step: TLPMatchModalStep;
    isEditing: boolean;
    editableBooleanQuery: string | null;
    selectedTLP: {
      id: number;
      name: string;
      html: string;
    };
    jobsList: {
      data: { id: number; reference: string; title: string }[];
      isLoading: boolean;
    };
  };
}

export const initialEngageState: EngageState = {
  selectedTab: 0,
  indicatorWidth: 0,
  snackbarState: {
    message: '',
    state: 'success'
  },
  searchQuery: '',
  booleanQuery: null,
  searchType: 'none',
  searchSettings: {
    page: 1,
    limit: 10,
    sort: 'last_applied_on',
    sort_order: 'desc'
  },
  searchFilters: {},
  tempSearchFilters: initialSearchFilters,
  searchTable: {
    canScroll: [false, true],
    tableHovered: false,
    density: 'Default',
    columns: searchTableColumns,
    selectedItemIds: [],
    selectedItems: [],
    actions: {
      selectedItem: null,
      anchorEl: null,
      isOpen: {
        addNote: false,
        viewAttachments: false,
        moreOptions: false,
        email: false,
        candidatePool: false
      }
    },
    modalsOpen: actionDrawerModals.reduce((acc, { id }) => {
      acc[id] = false;
      return { ...acc, exportCsv: false, addSearch: false, candidatePool: false };
    }, {})
  },
  dateFieldRange: {
    from: null,
    to: null
  },
  tlpMatchModalState: {
    open: false,
    step: 'select',
    isEditing: false,
    editableBooleanQuery: '',
    selectedTLP: {
      id: -1,
      name: '',
      html: ''
    },
    jobsList: {
      data: [],
      isLoading: false
    }
  }
};

export type EngageAction =
  | { type: 'SET_SELECTED_TAB'; payload: number }
  | { type: 'SET_INDICATOR_WIDTH'; payload: number | undefined }
  | { type: 'SET_SNACKBAR'; payload: ISnackbarInput }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'EDIT_BOOLEAN_QUERY' }
  | { type: 'CANCEL_EDIT_BOOLEAN_QUERY' }
  | { type: 'CONFIRM_STRING_MATCH_SEARCH'; payload: string }
  | { type: 'SET_EDITABLE_BOOLEAN_QUERY'; payload: string }
  | { type: 'CONFIRM_EDIT_BOOLEAN_QUERY'; payload: string }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'OPEN_TLP_MATCH_MODAL' }
  | { type: 'CLOSE_TLP_MATCH_MODAL' }
  | { type: 'SET_TLP_MATCH_MODAL_STEP'; payload: TLPMatchModalStep }
  | { type: 'SET_SELECTED_TLP'; payload: { id: number; name: string; html: string } }
  | { type: 'SET_JOBS_LIST'; payload: { id: number; reference: string; title: string }[] }
  | { type: 'SET_JOBS_LIST_LOADING' }
  | { type: 'SET_COUNTRIES_DATA'; payload: ICountries[] }
  | {
      type: 'SET_CANDIDATE_POOL_MODAL_OPEN';
      payload: { name: string; isOpen: boolean; item: TCandidate | null };
    }
  | TableScrollAction
  | TableSelectionAction<TCandidate>
  | TableColumnAction<typeof searchTableColumns>
  | TablePaginationAction<(typeof searchTableColumns)[number]['id']>
  | TableFilterAction<ITempFilters | TableFilter>
  | TableActionItemAction<TCandidate>
  | DateRangeAction
  | TableActionDrawerAction;

export const engageReducer: Reducer<EngageState, EngageAction> = (
  state: EngageState,
  action: EngageAction
) => {
  switch (action.type) {
    case 'SET_SELECTED_TAB':
      return {
        ...state,
        selectedTab: action.payload
      };
    case 'SET_INDICATOR_WIDTH': {
      if (action.payload === undefined) {
        return state;
      } else {
        return {
          ...state,
          indicatorWidth: action.payload - (state.indicatorWidth ? 32 : 37)
        };
      }
    }
    case 'SET_SNACKBAR':
      return {
        ...state,
        snackbarState: action.payload
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload
      };
    case 'EDIT_BOOLEAN_QUERY':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          isEditing: true,
          open: true,
          editableBooleanQuery: state.booleanQuery,
          step: 'edit'
        }
      };
    case 'CANCEL_EDIT_BOOLEAN_QUERY':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          isEditing: false,
          open: false,
          editableBooleanQuery: ''
        }
      };
    case 'SET_EDITABLE_BOOLEAN_QUERY':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          editableBooleanQuery: action.payload
        }
      };
    case 'CONFIRM_STRING_MATCH_SEARCH':
      return {
        ...state,
        booleanQuery: action.payload,
        searchType: 'string_match'
      };
    case 'CONFIRM_EDIT_BOOLEAN_QUERY':
      return {
        ...state,
        booleanQuery: action.payload,
        searchType: state.tlpMatchModalState.isEditing ? state.searchType : 'tlp_match',
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          open: false,
          isEditing: false
        }
      };
    case 'CLEAR_SEARCH':
      return {
        ...state,
        booleanQuery: null,
        searchType: 'none',
        searchQuery: ''
      };
    case 'SET_CAN_SCROLL':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          canScroll: action.payload
        }
      };
    case 'SET_TABLE_HOVERED':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          tableHovered: action.payload
        }
      };
    case 'OPEN_TLP_MATCH_MODAL':
      return {
        ...state,
        tlpMatchModalState: {
          ...initialEngageState.tlpMatchModalState,
          step: 'select',
          open: true
        }
      };
    case 'CLOSE_TLP_MATCH_MODAL':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          open: false
        }
      };
    case 'SET_TLP_MATCH_MODAL_STEP':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          step: action.payload
        }
      };
    case 'SET_JOBS_LIST_LOADING':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          jobsList: {
            ...state.tlpMatchModalState.jobsList,
            isLoading: true
          }
        }
      };
    case 'SET_JOBS_LIST':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          jobsList: {
            ...state.tlpMatchModalState.jobsList,
            isLoading: false,
            data: action.payload
          }
        }
      };
    case 'SET_SELECTED_TLP':
      return {
        ...state,
        tlpMatchModalState: {
          ...state.tlpMatchModalState,
          selectedTLP: action.payload
        }
      };
    case 'ADD_SELECTED_ITEM':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          ...addSelected(
            state.searchTable.selectedItems,
            state.searchTable.selectedItemIds,
            action.payload
          )
        }
      };
    case 'REMOVE_SELECTED_ITEM':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          ...removeSelected(
            state.searchTable.selectedItems,
            state.searchTable.selectedItemIds,
            action.payload
          )
        }
      };
    case 'ADD_ALL_PAGE_ITEMS':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          ...addAllPageItems(state.searchTable.selectedItems, action.payload)
        }
      };
    case 'REMOVE_ALL_SELECTED_ITEMS':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          selectedItemIds: [],
          selectedItems: []
        }
      };
    case 'REMOVE_ALL_PAGE_ITEMS':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          ...removeAllPageItems(state.searchTable.selectedItems, action.payload)
        }
      };
    case 'SELECT_ALL_ITEMS':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          selectedItemIds: action.payload
        }
      };
    case 'SET_COUNTRIES_DATA':
      return {
        ...state,
        countriesData: action.payload
      };
    case 'SET_DENSITY':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          density: action.payload
        }
      };
    case 'SET_SORT':
      return {
        ...state,
        searchSettings: {
          ...state.searchSettings,
          sort: action.payload.sortBy,
          sort_order: action.payload.sortOrder
        }
      };
    case 'SET_COLUMNS':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          columns: action.payload
        }
      };
    case 'SET_PAGE':
      return {
        ...state,
        searchSettings: {
          ...state.searchSettings,
          page: action.payload
        }
      };
    case 'SET_LIMIT':
      return {
        ...state,
        searchSettings: {
          ...state.searchSettings,
          limit: action.payload
        }
      };
    case 'SET_TEMP_FILTERS':
      return {
        ...state,
        tempSearchFilters: {
          ...state.tempSearchFilters,
          ...action.payload
        }
      };
    case 'SET_DATE_RANGE_TEMP_FILTER':
      return {
        ...state,
        tempSearchFilters: {
          ...state.tempSearchFilters,
          date1: action.payload.from,
          date2: action.payload.to
        }
      };
    case 'SET_DATE_FIELD_RANGE':
      return {
        ...state,
        dateFieldRange: action.payload
      };
    case 'SET_FILTERS': {
      const convertedFilters = Object.entries(state.tempSearchFilters).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              if (key === 'country' || key === 'state') {
                acc[key] = value.map((f) => f.name);
              } else {
                acc[key] = value[0].id ? value.map((f) => f.id) : value;
              }
            }
          } else if (value) {
            if (key === 'date1' || key === 'date2') {
              acc[key] = moment(value).format('YYYY-MM-DD');
            } else {
              acc[key] = value;
            }
          }
          return acc;
        },
        {} as ISearchFilters
      );
      return {
        ...state,
        searchFilters: convertedFilters
      };
    }
    case 'RESET_FILTERS':
      return {
        ...state,
        searchFilters: {},
        tempSearchFilters: initialSearchFilters,
        dateFieldRange: {
          from: null,
          to: null
        }
      };
    case 'SELECT_ACTION_ITEM':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          actions: {
            ...state.searchTable.actions,
            selectedItem: action.payload.item,
            anchorEl: action.payload.anchorEl,
            isOpen: {
              ...state.searchTable.actions.isOpen,
              [action.payload.name]: true
            }
          }
        }
      };
    case 'CLOSE_ACTION_ITEM':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          actions: {
            ...state.searchTable.actions,
            selectedItem: null,
            isOpen: {
              ...state.searchTable.actions.isOpen,
              [action.payload]: false
            }
          }
        }
      };
    case 'SET_ACTION_MODAL_OPEN':
      return {
        ...state,
        searchTable: {
          ...state.searchTable,
          modalsOpen: {
            ...state.searchTable.modalsOpen,
            [action.payload.name]: action.payload.isOpen
          }
        }
      };
    default:
      return state;
  }
};
